import React, { useEffect, useRef, useState } from 'react';
import { ScheduleContextProvider } from '../../../../platform/src/context/ScheduleContext';
import { useEventContext } from '../../../../platform/src/context/EventContext';
import { useLocaleContext } from '../../../../platform/src/context/LocaleContext';
import { usePdf } from '../../../../platform/src/hooks/usePdf';
import { useWidgetContext } from '../../../../platform/src/context/WidgetContextProvider';
import * as Api from '../../../../api';
import createCssVariables from '../../../widgetRegistration/src/core/utils/createCssVariables';
import { getCssVarsModel } from '../css-vars-model';
import { VIEW_TYPE_WIDGET } from '../../../../platform/src/constants/view-types';
import ScheduleFilters from '../../../../platform/src/components/Schedule/ScheduleFilters';
import Schedule from '../../../../platform/src/components/Schedule';
import PageContainer from '../../../../platform/src/components/PageContainer';
import { CButton } from '../../../../platform/src/components/UI';
import SchedulePdf from '../../../../platform/src/components/Pdf/Schedule';
import LoadingMessage from '../../../../platform/src/components/LoadingMessage';
import QuestionWriteForm from '../../../../platform/src/controllers/Questions/QuestionWriteForm';

import './style.scss';

const getFontSizeGeneral = (settings) => {
    if (!settings || !String(settings.fontSizeGeneral).trim()) {
        return '14px';
    }

    return settings.fontSizeGeneral;
};

const WidgetView = ({ widgetKey }) => {
    const { event, pages } = useEventContext();
    const { setWidgetSettings: setSettingsWidget } = useWidgetContext();
    const $locale = useLocaleContext();

    const widgetRef = useRef(null);

    const [widgetSettings, setWidgetSettings] = useState(null);
    const [isReady, setIsReady] = useState(false);
    const page = pages.find(({ type }) => type === 'program');
    const { target, savePdf, pdfSettings } = usePdf({
        locale: $locale.name,
        eventKey: event.eventKey,
    });

    // Получить настройки виджета, если указан widgetKey при инициализации
    useEffect(() => {
        if (!!widgetKey) {
            fetchWidgetSettings();
        }
    }, [widgetKey]);

    // Слушатель настроек для превью в КП
    useEffect(() => {
        const messageHandler = (event) => {
            try {
                const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

                if (!data) return;

                if (data?.hasOwnProperty('widgetSettings')) {
                    setWidgetSettings(data.widgetSettings);
                    setSettingsWidget(data.widgetSettings);
                    setModalsVisibility(data.widgetSettings?.showModals);
                }
            } catch (error) {
                console.error(error);
            }
        };

        window.addEventListener('message', messageHandler);

        return () => {
            window.removeEventListener('message', messageHandler);
        };
    }, []);

    // Применение настроек виджета к виджету
    useEffect(() => {
        if (!widgetSettings) return;
        const model = getCssVarsModel(widgetSettings);

        Object.entries(model).forEach(([varName, value]) => {
            if (!!value) {
                document.documentElement.style.setProperty(varName, value);
            }
        });

        createCssVariables(document.documentElement, widgetSettings);
        setIsReady(true);

        if (!widgetSettings?.font) return;

        const link = document.createElement('link');
        const fontPath = widgetSettings.font.split(' ').join('');
        link.setAttribute('rel', 'stylesheet');
        link.setAttribute('type', 'text/css');
        link.setAttribute('href', `/files/fonts/${fontPath}/stylesheet.css`);
        document.head.appendChild(link);

        return () => {
            document.head.removeChild(link);
        };
    }, [widgetSettings]);

    async function fetchWidgetSettings() {
        try {
            const response = await Api.player.getWidgetSchedule(widgetKey);
            if (!!response) {
                setWidgetSettings(response);
            }
        } catch (error) {
            console.error(error);
        }
    }

    if (widgetKey && !isReady) {
        return <LoadingMessage isFullscreen isPreloader></LoadingMessage>;
    }

    return (
        <ScheduleContextProvider
            event={event}
            page={page}
            viewType={VIEW_TYPE_WIDGET}
            widgetSettings={widgetSettings}
            pdfSettings={pdfSettings}
        >
            <PageContainer>
                <section
                    ref={widgetRef}
                    className="schedule-page"
                    style={{ fontSize: getFontSizeGeneral(widgetSettings), width: '100%' }}
                >
                    <div className="schedule-page-programs" style={{ padding: '0.75em 0' }}>
                        {
                            (!!widgetSettings?.showTitle || !!widgetSettings?.showCommonAddToCalendar || !!widgetSettings?.showPdfExport) &&
                            <div className="schedule-page-programs__header">
                                {
                                    !!widgetSettings?.showTitle
                                        ? <h1 className="schedule-page-programs__header-title">
                                            {$locale.getProperty(widgetSettings || {}, 'title') || $locale.getProperty('title.page.schedule')}
                                        </h1>
                                        : <div />
                                }
                                <div className="schedule-page-programs__header-actions">
                                    {
                                        !!widgetSettings?.showCommonAddToCalendar &&
                                        <CButton
                                            label={$locale.get('label.calendar.add')}
                                            icon="calendar"
                                            iconStyles="calendar"
                                            onClick={() => window.open(`/api/player/event/${event.eventKey}/calendar.ics?locale=${$locale.name}&hideAddress=true`, '_blank')}
                                        />
                                    }
                                    {
                                        !!widgetSettings?.showPdfExport &&
                                        <CButton
                                            label={$locale.get('label.pdf.download')}
                                            icon="download"
                                            theme="accent"
                                            className="pdf-btn"
                                            onClick={savePdf}
                                        />
                                    }
                                </div>
                            </div>
                        }
                        {!!widgetSettings?.showSubtitle &&
                            <div className="schedule-page-programs__subheader">
                                <p>
                                    {$locale.getProperty(widgetSettings || {}, 'subtitle')}
                                </p>
                            </div>
                        }
                        <ScheduleFilters
                            viewsList={widgetSettings?.scheduleViews}
                            hasViewsFilter={true}
                            hasSearch={!!widgetSettings?.showSearch}
                            hasFormatFilter={!!widgetSettings?.showFormatFilter}
                            hasTagsFilter={!!widgetSettings?.showTagsFilter}
                        />
                        <Schedule />
                    </div>
                </section>
            </PageContainer>
            <SchedulePdf
                ref={target}
                event={event}
                pdfSettings={pdfSettings}
            />
        </ScheduleContextProvider>
    );
};

export default WidgetView;
